/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useContext, useState } from 'react';
import { noop } from 'lodash';
import { css, StyleSheet } from 'aphrodite';
import { ActionButton, BrandColors, Header } from '@blend/enterprise-components';

import { searchForLoans } from 'blb-api/loans';
import { getUserPreference, updateUserPreference } from 'blb-api/users';
import { AssignmentPreferencesModal } from 'blb-components/AuthenticatedApp/AssignmentPreferencesModal';
import { TypeaheadSearch } from 'blb-components/AuthenticatedApp/TypeaheadSearch';
import { searchWidth } from 'blb-styles/constants';
import { MeContext } from 'blb-contexts/Me';
import { getConfig } from 'blb-utils/config';
import { sleep } from 'blb-utils/misc';

import { PipelineSearchResult } from './PipelineSearchResult';

export const styles = StyleSheet.create({
  assignmentToggleBtnWrapper: {
    margin: '0 8px',
  },
  buttonGroup: {
    display: 'flex',
  },
  header: {
    alignItems: 'center',
    backgroundColor: BrandColors.CLOUD_10,
    borderBottom: `1px solid ${BrandColors.CLOUD_30}`,
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 16,
    padding: `42px 40px 20px`,
  },
  // remove below class when removing autoAssignmentUI FF
  headerOld: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    padding: `42px 40px 20px`,
  },
  headerText: {},
  searchWrapper: {
    width: searchWidth,
  },
});

export const PIPELINE_REFRESH_INTERVAL = 5000;

const assignmentToggleButtonLabel = (canBeAssigned: boolean | null) => {
  switch (canBeAssigned) {
    case true:
      return 'Stop assigning to me';
    case false:
      return 'Start assigning to me';
    case null:
      return 'Loading status…';
  }
};

const assignmentToggleButtonTheme = (canBeAssigned: boolean | null): 'secondary' | 'primary' => {
  return canBeAssigned === true ? 'secondary' : 'primary';
};

export namespace PipelinePageHeader {
  export interface Props {
    fetchPipelineTopics: () => Promise<number | undefined>;
    setShowAssignmentOverlay?: React.Dispatch<React.SetStateAction<boolean>>;
  }
}

export const PipelinePageHeader: React.FC<PipelinePageHeader.Props> = ({
  fetchPipelineTopics,
  setShowAssignmentOverlay,
}) => {
  const me = useContext(MeContext);
  const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState<boolean>(false);

  const shouldShowAutoAssignments = me!.principalType === 'ops_desk_agent' && getConfig().autoAssignmentUI!;

  if (shouldShowAutoAssignments) {
    const [canBeAssigned, setCanBeAssigned] = useState<boolean | null>(null);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState<boolean>(true);

    React.useEffect(() => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      (async () => {
        const { data: userPrefs } = await getUserPreference(me!.principalId);
        setCanBeAssigned(userPrefs.canBeAssigned);
        setIsUpdatingStatus(false);
      })();
    }, []);

    /**
     * Since Start/Stop auto assigning triggers a bulk shuffling job, we are showing the
     * `Updating Assignments` overlay until the updates are completed. Each updates in assignment
     * results in update to Elastic Search data. So, we are periodically refreshing the pipeline to
     * fetch the latest data from Elastic Search, until there are no new data. The total number of
     * data in pipeline is used as a proxy for updates. So, if there are consecutive fetches with
     * same number of topics, we assume that the updates have completed.
     */
    const fetchPipelineTopicsUntilUpdatingIsCompleted = async (isAssigning: boolean): Promise<void> => {
      // setting inital topics count as -1 so that it does not exit on first fetch when assigning.
      let currentTopicsCount = -1;
      const MAX_FETCHES = 10;

      const checkForNewUpdates = async (currentFetchIndex = 0) => {
        const updatedTopicsCount = await fetchPipelineTopics();
        if (updatedTopicsCount === undefined) {
          throw new Error('Fetching pipeline topics failed');
        }

        const tooManyFetches = currentFetchIndex >= MAX_FETCHES;
        const noLongerUpdating = updatedTopicsCount === (isAssigning ? currentTopicsCount : 0);
        if (noLongerUpdating || tooManyFetches) {
          setShowAssignmentOverlay && setShowAssignmentOverlay(false);
          return;
        }

        currentTopicsCount = updatedTopicsCount;
        await sleep(PIPELINE_REFRESH_INTERVAL);
        await checkForNewUpdates(currentFetchIndex + 1);
      };

      await checkForNewUpdates();
      return;
    };

    const toggleAssignmentStatus = async () => {
      setIsUpdatingStatus(true);
      try {
        const newStatus = !canBeAssigned;
        await updateUserPreference(me!.principalId, { canBeAssigned: newStatus, service: 'ECLOSE' });
        if (setShowAssignmentOverlay) {
          setShowAssignmentOverlay(true);
          await fetchPipelineTopicsUntilUpdatingIsCompleted(newStatus);
        }
        setCanBeAssigned(newStatus);
      } finally {
        setIsUpdatingStatus(false);
      }
    };

    return (
      <>
        <header className={css(styles.header)} id="it-Pipeline-header">
          <Header size={2}>Pipeline</Header>
          <div className={css(styles.buttonGroup)}>
            <div className={css(styles.assignmentToggleBtnWrapper)}>
              <ActionButton
                id="it-PipelineFilter-assignmentToggle-btn"
                isLoading={isUpdatingStatus}
                label={assignmentToggleButtonLabel(canBeAssigned)}
                onClick={toggleAssignmentStatus}
                theme={assignmentToggleButtonTheme(canBeAssigned)}
              />
            </div>
            <ActionButton
              id="it-PipelineFilter-assignmentPreferences-btn"
              label="Preferences"
              onClick={() => {
                setIsPreferencesModalOpen(true);
              }}
              theme="tertiary"
            />
          </div>
        </header>
        {isPreferencesModalOpen && (
          <AssignmentPreferencesModal
            handleClose={() => setIsPreferencesModalOpen(false)}
            isOpen={isPreferencesModalOpen}
          />
        )}
      </>
    );
  }

  return (
    <div className={css(styles.headerOld)} id="it-Pipeline-header">
      <Header size={2}>Loans</Header>
      <div className={css(styles.searchWrapper)}>
        <TypeaheadSearch
          ResultComponent={PipelineSearchResult}
          id="pipeline-search"
          name="Pipeline"
          onResultClick={noop}
          placeholder="Search for loans"
          searchFunction={searchForLoans}
        />
      </div>
    </div>
  );
};
